import React, { useState } from 'react';
import { motion } from 'framer-motion';

const springEasing = [0.22, 1, 0.36, 1];

const CaseStudyCard = ({ study, index }) => {
  const [expanded, setExpanded] = useState(false);

  const savingsPct = Math.round(((study?.costBefore - study?.costAfter) / study?.costBefore) * 100);
  const annualSavings = (study?.costBefore - study?.costAfter) * 12;

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.55, ease: springEasing }}
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(224,228,232,0.08)',
        borderRadius: '2px',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Top accent bar */}
      <div style={{ height: '3px', background: `linear-gradient(to right, #FF5722, #004F98)` }} />
      {/* Header */}
      <div style={{ padding: '32px 32px 24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          {/* Company logo placeholder */}
          <div style={{
            width: '64px', height: '64px',
            background: study?.logoColor || 'rgba(255,87,34,0.12)',
            border: `2px solid ${study?.logoColor || 'rgba(255,87,34,0.3)'}`,
            borderRadius: '2px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0
          }}>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 700, color: study?.logoTextColor || '#FF5722' }}>{study?.logoInitials}</span>
          </div>
          <div>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '30px', fontWeight: 700, color: 'var(--color-foreground)', letterSpacing: '-0.02em', marginBottom: '4px' }}>{study?.company}</h3>
            <span style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)' }}>{study?.industry}</span>
          </div>
        </div>
        {/* Savings badge */}
        <div style={{ background: 'rgba(255,87,34,0.12)', border: '1px solid rgba(255,87,34,0.3)', borderRadius: '2px', padding: '12px 20px', textAlign: 'center', flexShrink: 0 }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '28px', fontWeight: 700, color: '#FF5722', lineHeight: 1 }}>{savingsPct}%</div>
          <div style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', marginTop: '4px' }}>Besparelse</div>
        </div>
      </div>
      {/* Challenge */}
      <div style={{ padding: '0 32px 24px' }}>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: '16px', fontWeight: 500, color: 'var(--color-muted-foreground)', lineHeight: 1.7 }}>{study?.challenge}</p>
      </div>
      {/* Before / After toll costs */}
      <div style={{ margin: '0 32px 24px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(224,228,232,0.06)', borderRadius: '2px', padding: '24px', display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '16px', alignItems: 'center' }}>
        {/* Before */}
        <div>
          <div style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', marginBottom: '8px' }}>Månedlig kostnad FØR</div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 400, color: 'rgba(224,228,232,0.5)', textDecoration: 'line-through' }}>
            kr {study?.costBefore?.toLocaleString('nb-NO')}
          </div>
          <div style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)', marginTop: '4px' }}>{study?.vehicleCount} kjøretøy</div>
        </div>
        {/* Arrow */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <svg width="32" height="16" viewBox="0 0 32 16" fill="none">
            <path d="M0 8 L24 8 M20 4 L28 8 L20 12" stroke="#FF5722" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ fontFamily: 'var(--font-caption)', fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', color: '#FF5722', textTransform: 'uppercase' }}>Spart</span>
        </div>
        {/* After */}
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', marginBottom: '8px' }}>Månedlig kostnad ETTER</div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', fontWeight: 600, color: 'var(--color-foreground)', fontVariantNumeric: 'tabular-nums' }}>
            kr {study?.costAfter?.toLocaleString('nb-NO')}
          </div>
          <div style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: '#FF5722', marginTop: '4px', fontWeight: 600 }}>−kr {(study?.costBefore - study?.costAfter)?.toLocaleString('nb-NO')}/mnd</div>
        </div>
      </div>
      {/* Annual savings highlight */}
      <div style={{ margin: '0 32px 24px', background: 'linear-gradient(135deg, rgba(255,87,34,0.08) 0%, rgba(0,79,152,0.08) 100%)', border: '1px solid rgba(255,87,34,0.15)', borderRadius: '2px', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', marginBottom: '6px' }}>Årlig besparelse</div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', fontWeight: 700, color: 'var(--color-foreground)', fontVariantNumeric: 'tabular-nums' }}>kr {annualSavings?.toLocaleString('nb-NO')}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', marginBottom: '6px' }}>Per kjøretøy/mnd</div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 500, color: 'var(--color-muted-foreground)', fontVariantNumeric: 'tabular-nums' }}>kr {Math.round((study?.costBefore - study?.costAfter) / study?.vehicleCount)?.toLocaleString('nb-NO')}</div>
        </div>
      </div>
      {/* Expand toggle */}
      <button
        onClick={() => setExpanded(p => !p)}
        style={{ width: '100%', background: 'none', border: 'none', borderTop: '1px solid rgba(224,228,232,0.06)', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}
      >
        <span>{expanded ? 'Skjul detaljer' : 'Se løsningsdetaljer'}</span>
        <motion.span animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.3 }} style={{ display: 'inline-block' }}>▼</motion.span>
      </button>
      {/* Expanded details */}
      <motion.div
        initial={false}
        animate={{ height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }}
        transition={{ duration: 0.4, ease: springEasing }}
        style={{ overflow: 'hidden' }}
      >
        <div style={{ padding: '24px 32px 32px' }}>
          <h4 style={{ fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-foreground)', marginBottom: '16px' }}>Løsning implementert</h4>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {study?.solutions?.map((sol, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ width: '6px', height: '6px', background: '#FF5722', borderRadius: '1px', flexShrink: 0, marginTop: '6px' }} />
                <span style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 500, color: 'var(--color-muted-foreground)', lineHeight: 1.6 }}>{sol}</span>
              </li>
            ))}
          </ul>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CaseStudyCard;
